import { useEffect, useState, useContext } from "react";
import { useParams, useNavigate } from "react-router";
import axios from "axios";

import { AppContext } from '../../context.js';
import { ArrowLeft, ChevronDown, MoreVertical, Refresh, CalendarEdit } from "../../files/icons/index.js";
import Loading from "../components/loading.js";
import Helmet from "../components/helmet.js";
import { Status, Button, PatientAvatar } from '../components/component.js';
import Editor from "../components/editor.js";
import Drop from "../components/drop.js";
import { DatePicker } from "../components/datePicker.js";
import RecurrencePicker from "../components/recurrencePicker.js";
import PhysicianPicker from "../components/physicianPicker.js";
import { FullName } from '../../utils/string.js';
import { DateToFormat, DatetimeToDate, InRangeUTC } from "../../utils/datetime.js";
import { isCognitiveStagingTemplate } from '../../utils/assessments.js';

function Assessment() {
  const context = useContext(AppContext);
  const navigate = useNavigate();
  
  let { id } = useParams();

  const [loading, setLoading] = useState(true);

  const [assessment, setAssessment] = useState(null);
  const [users, setUsers] = useState([]);
  const [settings, setSettings] = useState(null);

  const [parentData, setParentData] = useState(null);
  const [parentType, setParentType] = useState(null); // 'patient' | 'intake' | null

  const [closeAssessmentStatusDrop, setCloseAssessmentStatusDrop] = useState(0);
  const [changeAssessmentStatusModalVisible, setChangeAssessmentStatusModalVisible] = useState(false);
  const [changeAssessmentStatusModalData, setChangeAssessmentStatusModalData] = useState(null);

  // Update assessment created at
  const [updateAssessmentCreatedAtData, setUpdateAssessmentCreatedAtData] = useState({
    visible: false,
    id: null,
    date: null,
    reason: null,
  });

  // Update assessment closed at
  const [updateAssessmentClosedAtData, setUpdateAssessmentClosedAtData] = useState({
    visible: false,
    id: null,
    date: null,
    reason: null,
  });

  useEffect(() => {
    let u = context.addSpiner();

    axios.all([
      axios.get(`/api/admin/assessments/${id}`),
      axios.post('/api/admin/physicians', {
        filter: {},
      }),
      axios.get('/api/admin/settings'),
    ]).then(axios.spread((assessmentResponse, usersResponse, settingsResponse) => {
      const assessmentData = assessmentResponse.data;

      setAssessment(assessmentData);
      setUsers(usersResponse.data.users);
      setSettings(settingsResponse.data);

      // Fetch parent data based on patientID or intakeID
      if (assessmentData.patientID) {
        // Fetch patient data
        axios.post('/api/admin/patients', {
          filter: { id: [assessmentData.patientID] }
        }).then((patientResponse) => {
          if (patientResponse.data.patients && patientResponse.data.patients.length > 0) {
            setParentData(patientResponse.data.patients[0]);
            setParentType('patient');
          }
          setLoading(false);
        }).catch((e) => {
          context.addError(e);
          setLoading(false);
        }).finally(() => {
          context.removeSpiner(u);
        });
      } else if (assessmentData.intakeID) {
        // Fetch intake data
        axios.post('/api/admin/intakes', {
          filter: { id: [assessmentData.intakeID] }
        }).then((intakeResponse) => {
          if (intakeResponse.data.intakes && intakeResponse.data.intakes.length > 0) {
            setParentData(intakeResponse.data.intakes[0]);
            setParentType('intake');
          }
          setLoading(false);
        }).catch((e) => {
          context.addError(e);
          setLoading(false);
        }).finally(() => {
          context.removeSpiner(u);
        });
      } else {
        // No parent to fetch
        setLoading(false);
        context.removeSpiner(u);
      }
    })).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    });
  }, [id]);

  // Header functions
  function changeAssessmentStatus(assessmentID, status) {
    if (status.reasons.length > 0) {
      setChangeAssessmentStatusModalData({status, assessmentID});
      setChangeAssessmentStatusModalVisible(true);
      return;
    }

    updateAssessmentStatus(assessmentID, status.id, null);
  }

  function changeAssessmentStatusConfirm(assessmentID, statusID, reasonID) {
    setChangeAssessmentStatusModalVisible(false);
    setChangeAssessmentStatusModalData(null);
    updateAssessmentStatus(assessmentID, statusID, reasonID);
  }

  function updateAssessmentStatus(assessmentID, statusID, reasonID) {
    let u = context.addSpiner();
    axios.post('/api/admin/assessments/updateStatus', {
      id: assessmentID,
      statusID: statusID,
      reasonID: reasonID,
    }).then((response) => {
      setAssessment({
        ...assessment, 
        statusType: response.data.statusType, 
        statusID: response.data.statusID, 
        statusName: response.data.statusName, 
        statusReasonID: response.data.statusReasonID, 
        statusReasonName: response.data.statusReasonName,
        scores: response.data.scores,
        closedAt: response.data.closedAt,
        closedBy: response.data.closedBy,
        closedByName: response.data.closedByName,
      });
      setCloseAssessmentStatusDrop(prev => prev + 1);
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function changeAssessmentTitle(id, text) {
    setAssessment(prevAssessment => ({ ...prevAssessment, title: text }));
  }

  function keyUpAssessmentTitle(e) {
    if (e.key === 'Enter') {
      e.target.blur();
    }
  }

  function updateAssessmentTitle(id) {
    let u = context.addSpiner();
    axios.post('/api/admin/assessments/updateTitle', {
      id: assessment.id,
      title: assessment.title
    }).then((response) => {
      
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function updateAssessmentDescription(text, id) {
    if (text === "" || text === "<p><br></p>") {
      text = null;
    }

    let u = context.addSpiner();
    axios.post('/api/admin/assessments/updateDescription', {
      id: assessment.id,
      description: text
    }).then((response) => {
      setAssessment(prevAssessment => ({ ...prevAssessment, description: text }));
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function updateAssessmentUser(user, id) {
    let u = context.addSpiner();
    axios.post('/api/admin/assessments/updateUser', {
      id: id,
      userID: user.id
    }).then((response) => {
      setAssessment(prevAssessment => ({ ...prevAssessment, userID: user.id, userName: FullName(user.firstName, user.middleName, user.lastName) }));
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function updateAssessmentDeadline(date, id) {
    let u = context.addSpiner();
    axios.post('/api/admin/assessments/updateDeadline', {
      id: id,
      deadline: date
    }).then((response) => {
      setAssessment(prevAssessment => ({ ...prevAssessment, deadlineAt: date }));
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function updateAssessmentRecurrence(assessmentID, intervalType, intervalDuration, fromType) {
    let u = context.addSpiner();
    axios.post('/api/admin/assessments/updateRecurrence', {
      id: assessmentID,
      intervalType: intervalType,
      intervalDuration: intervalDuration,
      fromType: fromType,
    }).then((response) => {
      setAssessment(prevAssessment => ({ ...prevAssessment, recurreAt: response.data.recurreAt }));
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function updateAssessmentCreatedAt() {
    let u = context.addSpiner();
    axios.post('/api/admin/assessments/updateCreatedAt', {
      id: updateAssessmentCreatedAtData.id,
      createdAt: updateAssessmentCreatedAtData.date,
      reason: updateAssessmentCreatedAtData.reason,
    }).then((response) => {
      setAssessment(prevAssessment => ({ ...prevAssessment, createdAt: updateAssessmentCreatedAtData.date }));
      setUpdateAssessmentCreatedAtData({ visible: false, id: null, date: null, reason: null });
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function updateAssessmentClosedAt() {
    let u = context.addSpiner();
    axios.post('/api/admin/assessments/updateClosedAt', {
      id: updateAssessmentClosedAtData.id,
      closedAt: updateAssessmentClosedAtData.date,
      reason: updateAssessmentClosedAtData.reason,
    }).then((response) => {
      setAssessment(prevAssessment => ({ ...prevAssessment, closedAt: updateAssessmentClosedAtData.date }));
      setUpdateAssessmentClosedAtData({ visible: false, id: null, date: null, reason: null });
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function renderAssessmentStatus(templateID, statusID) {
    const status = settings.assessmentTemplates.find((t) => t.id === templateID).statuses.find((s) => s.id === statusID);

    if (!status) {
      return <></>;
    }

    return (
      <Status color={status.color} muted={true} size={'small'} subtext={status.title} />
    );
  }

  function renderAssessmentNextStatuses(assessmentID, templateID, statusID) {
    const currentStatus = settings.assessmentTemplates.find((t) => t.id === templateID).statuses.find((s) => s.id === statusID);

    if (!currentStatus) {
      return <></>;
    }

    if (!currentStatus.next) {
      return (
        <div className="typo-medium-150">
          Assessment has no next statuses
        </div>
      );
    }

    return settings.assessmentTemplates.find((t) => t.id === templateID).statuses.filter((status) => currentStatus.next.includes(status.id)).map((status) => {
      return (
        <div key={status.id} className="side-item small typo-medium-150" onClick={() => changeAssessmentStatus(assessmentID, status)}>
          To <ArrowLeft height={14} width={16} color={'grey'} /> {renderAssessmentStatus(templateID, status.id)}
        </div>
      );
    })
  }

  // Form functions
  function changeAssessmentAnswerValue(questionID, answerValues) {
    const updatedQuestions = assessment.questions.map((question) => {
      if (question.id === questionID) {
        return { ...question, answerValues: answerValues };
      }
      return question;
    });

    setAssessment(prevAssessment => ({ ...prevAssessment, questions: updatedQuestions }));
  }

  function updateAssessmentAnswer(questionID, answerValues) {
    let u = context.addSpiner();
    axios.post('/api/admin/assessments/updateAnswer', {
      id: assessment.id,
      questionID: questionID,
      answerValues: answerValues,
    }).then((response) => {
      const updatedQuestions = assessment.questions.map((question) => {
        if (question.id === questionID) {
          return { ...question, answerValues: answerValues };
        }
        return question;
      });

      setAssessment(prevAssessment => ({ ...prevAssessment, questions: updatedQuestions }));
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function updateQuestionNote(note, questionID) {
    if (note === "" || note === "<p><br></p>") {
      note = null;
    }

    let u = context.addSpiner();
    axios.post('/api/admin/assessments/updateQuestionNote', {
      id: assessment.id,
      questionID: questionID,
      note: note,
    }).then((response) => {
      const updatedQuestions = assessment.questions.map((question) => {
        if (question.id === questionID) {
          return { ...question, note: note };
        }
        return question;
      });

      setAssessment(prevAssessment => ({ ...prevAssessment, questions: updatedQuestions }));
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  if (loading) {
    return (
      <>
        <Helmet title="Assessment" />
        <Loading />
      </>
    );
  }

  // Calculate deadline color
  var datePickerColor = "grey";
  if (assessment.deadlineAt && assessment.statusType !== "COMPLETED" && assessment.statusType !== "CANCELED") {
    const today = new Date();
    const deadlineDate = new Date(assessment.deadlineAt);
    
    const firstMomentOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0, 0);
    const lastMomentOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999);
    
    const tomorrow = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
    const firstMomentOfTomorrow = new Date(tomorrow.getFullYear(), tomorrow.getMonth(), tomorrow.getDate(), 0, 0, 0, 0);
    const lastMomentOfTomorrow = new Date(tomorrow.getFullYear(), tomorrow.getMonth(), tomorrow.getDate(), 23, 59, 59, 999);
    
    if (InRangeUTC(deadlineDate, firstMomentOfTomorrow, lastMomentOfTomorrow)) {
      datePickerColor = "yellow";
    }
    else if (InRangeUTC(deadlineDate, firstMomentOfToday, lastMomentOfToday) || new Date().getTime() > deadlineDate.getTime()) {
      datePickerColor = "red";
    }
  }

  return (
    <>
      <Helmet title={assessment.title || "Assessment"} />
      
      <div className="container">
        <div className="row mb-4">
          <div className="col-xs-12">
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex justify-content-start align-items-center">
                <span className="typo-hero-small">Assessment Overview</span>
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-xs-12">
            
            {/* <div className="d-flex justify-content-start align-items-center mb-3 mt-3">
              <div className="item grey" onClick={() => navigate(-1)}>
                <ArrowLeft height={16} width={16} color={'grey'} />
                <span className="typo-normal-150">Back</span>
              </div>
            </div> */}

            {/* Parent Information Header */}
            {parentData && (
              <div className="card mb-2">
                <div className="card-header sm">
                  <div className="row">
                    <div className="col-xs-6">
                      {parentType === 'patient' ? (
                        <div className="d-flex justify-content-start align-items-center">
                          <PatientAvatar
                            src={parentData.profileImageID}
                            id={parentData.id}
                            style={{height: 40, width: 40}}
                          />
                          <div className="ps-2">
                            <div className="typo-strong-175">
                              {FullName(parentData.firstName, parentData.middleName, parentData.lastName)}
                            </div>
                            <div className="typo-normal-150 text-muted">
                              {parentData.selfReferral
                                ? "Self Referral"
                                : (parentData.partnerName
                                  ? "By " + parentData.partnerName
                                  : "No Referral Source")}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="typo-strong-175">{parentData.template?.title || "Intake Form"}</div>
                      )}
                    </div>
                    <div className="col-xs-6 text-end">
                      {parentType === 'patient' ? (
                        <Status
                          color={parentData.statusType === "ACTIVE" ? "green" : parentData.statusType === "ARCHIVED" ? "grey" : "blue"}
                          muted={true}
                          size={'small'}
                          text={parentData.statusType === "ACTIVE" ? "Active" : parentData.statusType === "ARCHIVED" ? "Archived" : "Created"}
                        />
                      ) : (
                        <>
                          <Status
                            color={parentData.statusType === "TO_DO" ? "blue" : parentData.statusType === "COMPLETED" ? "green" : "grey"}
                            muted={true}
                            size={'small'}
                            text={parentData.statusName}
                            subtext={parentData.statusReasonName}
                          />
                          <div className="typo-normal-150 mt-2">
                            {!parentData.closedAt
                              ? "created at " + DateToFormat(DatetimeToDate(parentData.createdAt), context.dateFormat) + " " + DateToFormat(DatetimeToDate(parentData.createdAt), context.timeFormat === "us" ? "AMPM" : "HH:mm")
                              : "closed at " + DateToFormat(DatetimeToDate(parentData.closedAt), context.dateFormat) + " " + DateToFormat(DatetimeToDate(parentData.closedAt), context.timeFormat === "us" ? "AMPM" : "HH:mm")}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Assessment Header Card */}
            <div className="card mb-2">
              <div className="card-header sm">
                <div className="d-flex justify-content-between align-items-center">
                  <input 
                    type="text" 
                    className="input borderless typo-strong-200 w-100" 
                    placeholder="Title..." 
                    value={assessment.title} 
                    onChange={(e) => changeAssessmentTitle(assessment.id, e.target.value)} 
                    onBlur={() => updateAssessmentTitle(assessment.id)} 
                    onKeyUp={(e) => keyUpAssessmentTitle(e)} 
                  />
                  <div className="d-flex justify-content-end align-items-center w-25">
                    {renderAssessmentStatus(assessment.templateID, assessment.statusID)}
                    <Drop close={closeAssessmentStatusDrop} side={"right"} className="ms-1">
                      <div className="item rect">
                        <MoreVertical height={14} width={16} color={'grey'} />
                      </div>
                      <div className="card" style={{width: 200}}>
                        <div className="card-header sm">
                          {!isCognitiveStagingTemplate(assessment.templateID) && (
                            <>
                              <div className="typo-normal-150 text-muted">Change status:</div>
                              {renderAssessmentNextStatuses(assessment.id, assessment.templateID, assessment.statusID)}
                            </>
                          )}
                          <div className="typo-normal-150 text-muted">Change dates:</div>
                          <div className="side-item small typo-medium-150" onClick={() => setUpdateAssessmentCreatedAtData({ visible: true, id: assessment.id, date: assessment.createdAt, reason: null })}>
                            Change created at date
                          </div>
                          {assessment.closedAt && (
                            <div className="side-item small typo-medium-150" onClick={() => setUpdateAssessmentClosedAtData({ visible: true, id: assessment.id, date: assessment.closedAt, reason: null })}>
                              Change closed at date
                            </div>
                          )}
                          <div className="typo-normal-150 text-muted">Other actions:</div>
                          <div className="side-item small nowrap typo-medium-150" onClick={() => navigate("/export?assessmentID=" + assessment.id)}>
                            <div className="typo-normal-150">Export Assessment</div>
                          </div>
                        </div>
                      </div>
                    </Drop>
                  </div>
                </div>
                
                <Editor 
                  placeholder="Assessment description..."
                  value={assessment.description}
                  onChangeID={assessment.id}
                  onBlur={updateAssessmentDescription}
                  toolbar="limited"
                  className="text-editor borderless"
                />

                {(assessment.scores && assessment.scores.length > 0) && (
                  <div className="mt-1">
                    {assessment.scores.map((score, ai) => {
                      return (
                        <div key={"assessment_"+assessment.id+"_score_" + score.id} className="item grey me-2">
                          <div className="d-flex justify-content-start align-items-center">
                            <div>
                              <div className="typo-normal-150">{score.title}</div>
                              {score.description && (
                                <div className="typo-normal-150">{score.description}</div>
                              )}
                            </div>
                            <div className="typo-strong-150 ms-2">{score.score}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="d-flex justify-content-start align-items-center mt-2">
                  <div className="d-flex justify-content-start align-items-center me-3">
                    <span className="typo-normal-150">created at {DateToFormat(DatetimeToDate(assessment.createdAt), context.dateFormat)} {DateToFormat(DatetimeToDate(assessment.createdAt), context.timeFormat === "us" ? "AMPM" : "HH:mm")}</span>
                  </div>

                  {assessment.closedAt && (
                    <div className="d-flex justify-content-start align-items-center me-3">
                      <span className="typo-normal-150">closed at {DateToFormat(DatetimeToDate(assessment.closedAt), context.dateFormat)} {DateToFormat(DatetimeToDate(assessment.closedAt), context.timeFormat === "us" ? "AMPM" : "HH:mm")}</span>
                    </div>
                  )}
                </div>

                <div className="d-flex justify-content-start align-items-center mt-2">
                  <PhysicianPicker
                    physicianList={users}
                    physicianID={assessment.userID}
                    onChange={updateAssessmentUser}
                    onChangeID={assessment.id}
                    rightIcon={<ChevronDown height={16} width={16} color={'grey'} />}
                    className="me-2"
                  />

                  <DatePicker
                    date={assessment.deadlineAt}
                    onChange={updateAssessmentDeadline}
                    onChangeID={assessment.id}
                    leftIcon={<CalendarEdit height={16} width={16} color={'grey'} />}
                    rightIcon={<ChevronDown height={16} width={16} color={'grey'} />}
                    className={"me-2 " + datePickerColor}
                  />

                  <RecurrencePicker
                    recurreAt={assessment.recurreAt}
                    onChange={updateAssessmentRecurrence}
                    onChangeID={assessment.id}
                    leftIcon={<Refresh height={16} width={16} color={'grey'} />}
                    rightIcon={<ChevronDown height={16} width={16} color={'grey'} />}
                    className="me-2"
                  />
                </div>
              </div>
            </div>

            {/* Form Section */}
            {assessment.questions.map((question, qi) => {
              return (
                <div key={"question_" + qi} className="card mb-2">
                  <div className="card-header sm">
                    <div className="typo-strong-175">{question.title}</div>
                    {question.description && (
                      <div className="typo-normal-150">{question.description}</div>
                    )}

                    {question.answerType === "text" && (() => {
                      const DECLINE_TEXT = "I choose not to answer this question";
                      const declined = question.answerValues && question.answerValues.length > 0 && question.answerValues[0] === DECLINE_TEXT;
                      return (
                        <div className="mt-1">
                          <input
                            type="text"
                            className="input borderless typo-normal-150 w-100"
                            placeholder="Answer..."
                            disabled={declined}
                            value={declined ? "" : ((question.answerValues && question.answerValues.length > 0) ? question.answerValues[0] : "")}
                            onChange={(e) => changeAssessmentAnswerValue(question.id, e.target.value ? [e.target.value] : [])}
                            onBlur={() => updateAssessmentAnswer(question.id, question.answerValues || [])}
                          />
                          {question.allowDecline && (
                            <label className="d-flex align-items-center mt-1 typo-normal-150" style={{cursor: "pointer"}}>
                              <input
                                type="checkbox"
                                className="me-2"
                                checked={!!declined}
                                onChange={(e) => updateAssessmentAnswer(question.id, e.target.checked ? [DECLINE_TEXT] : [])}
                              />
                              I choose not to answer this question
                            </label>
                          )}
                        </div>
                      );
                    })()}

                    {question.answerType === "options" && (
                      <div className="mt-1">
                        {question.answerOptions.map((option, oi) => {
                          const isSelected = question.answerValues && question.answerValues.includes(oi+"");
                          return (
                            <div 
                              key={"answer_" + oi} 
                              className={"item typo-normal-150 me-2 mb-1 " + (isSelected ? "blue" : "grey")} 
                              onClick={() => updateAssessmentAnswer(question.id, [oi+""])}
                            >
                              {option}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {question.answerType === "optionsColumn" && (
                      <div className="d-flex flex-column justify-content-start align-items-start mt-1">
                        {question.answerOptions.map((option, oi) => {
                          const isSelected = question.answerValues && question.answerValues.includes(oi+"");
                          return (
                            <div 
                              key={"answer_" + oi} 
                              className={"item typo-normal-150 me-2 mb-1 " + (isSelected ? "blue" : "grey")} 
                              onClick={() => updateAssessmentAnswer(question.id, [oi+""])}
                            >
                              {option}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {question.answerType === "checkboxes" && (
                      <div className="mt-1">
                        {question.answerOptions.map((option, oi) => {
                          const isSelected = question.answerValues && question.answerValues.includes(oi+"");
                          return (
                            <div 
                              key={"answer_" + oi} 
                              className={"item typo-normal-150 me-2 mb-1 " + (isSelected ? "blue" : "grey")} 
                              onClick={() => {
                                const currentValues = question.answerValues || [];
                                const optionIndex = oi+"";
                                let newValues;
                                if (currentValues.includes(optionIndex)) {
                                  // Remove from array
                                  newValues = currentValues.filter(v => v !== optionIndex);
                                } else {
                                  // Add to array
                                  newValues = [...currentValues, optionIndex];
                                }
                                updateAssessmentAnswer(question.id, newValues);
                              }}
                            >
                              {option}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {question.answerType === "checkboxesColumn" && (
                      <div className="d-flex flex-column justify-content-start align-items-start mt-1">
                        {question.answerOptions.map((option, oi) => {
                          const isSelected = question.answerValues && question.answerValues.includes(oi+"");
                          return (
                            <div 
                              key={"answer_" + oi} 
                              className={"item typo-normal-150 me-2 mb-1 " + (isSelected ? "blue" : "grey")} 
                              onClick={() => {
                                const currentValues = question.answerValues || [];
                                const optionIndex = oi+"";
                                let newValues;
                                if (currentValues.includes(optionIndex)) {
                                  // Remove from array
                                  newValues = currentValues.filter(v => v !== optionIndex);
                                } else {
                                  // Add to array
                                  newValues = [...currentValues, optionIndex];
                                }
                                updateAssessmentAnswer(question.id, newValues);
                              }}
                            >
                              {option}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <div className="card-header sm">
                    <Editor 
                      placeholder="Note..."
                      value={question.note}
                      onChangeID={question.id}
                      onBlur={updateQuestionNote}
                      toolbar="limited"
                      className="text-editor borderless"
                    />
                  </div>
                </div>
              );
            })}

          </div>
        </div>
      </div>

      {/* Update Created At Modal */}
      {updateAssessmentCreatedAtData.visible && (
        <div className="fixed-bottom modal-bg" onClick={(e) => setUpdateAssessmentCreatedAtData({ visible: false })}>
          <div className="container-fluid" style={{paddingTop: '5%'}}>
            <div className="row">
              <div className="col-xs-10 col-xs-offset-1 col-lg-4 col-lg-offset-4">
                <div className="card shadowmore" onClick={(e) => {e.stopPropagation()}}>
                  <div className="card-header">
                    <span className="typo-header-3xLarge">Update Assessment Creation Date</span><br/>
                    <span className="typo-normal-175">Select the correct date and reason of creation and click update button</span>
                  </div>
                  <div className="card-content">
                    <div className="d-flex justify-content-start align-items-center">
                      <DatePicker
                        date={updateAssessmentCreatedAtData.date}
                        onChange={(date) => setUpdateAssessmentCreatedAtData({...updateAssessmentCreatedAtData, date: date})}
                        onChangeID={null}
                        leftIcon={<CalendarEdit height={16} width={16} color={'grey'} />}
                        rightIcon={<ChevronDown height={16} width={16} color={'grey'} />}
                        className="me-2"
                      />
                      <input 
                        type="text" 
                        className="input borderless typo-normal-150 w-100 ms-2" 
                        placeholder="Reason..." 
                        value={updateAssessmentCreatedAtData.reason} 
                        onChange={(e) => setUpdateAssessmentCreatedAtData({...updateAssessmentCreatedAtData, reason: e.target.value})} 
                      />
                    </div>
                  </div>  
                  <div className="card-content text-end">
                    <Button onClick={updateAssessmentCreatedAt} color="blue" text="Update" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Update Closed At Modal */}
      {updateAssessmentClosedAtData.visible && (
        <div className="fixed-bottom modal-bg" onClick={(e) => setUpdateAssessmentClosedAtData({ visible: false })}>
          <div className="container-fluid" style={{paddingTop: '5%'}}>
            <div className="row">
              <div className="col-xs-10 col-xs-offset-1 col-lg-4 col-lg-offset-4">
                <div className="card shadowmore" onClick={(e) => {e.stopPropagation()}}>
                  <div className="card-header">
                    <span className="typo-header-3xLarge">Update Assessment Completion Date</span><br/>
                    <span className="typo-normal-175">Select the correct date and reason of completion and click update button</span>
                  </div>
                  <div className="card-content">
                    <div className="d-flex justify-content-start align-items-center">
                      <DatePicker
                        date={updateAssessmentClosedAtData.date}
                        onChange={(date) => setUpdateAssessmentClosedAtData({...updateAssessmentClosedAtData, date: date})}
                        onChangeID={null}
                        leftIcon={<CalendarEdit height={16} width={16} color={'grey'} />}
                        rightIcon={<ChevronDown height={16} width={16} color={'grey'} />}
                        className="me-2"
                      />
                      <input 
                        type="text" 
                        className="input borderless typo-normal-150 w-100 ms-2" 
                        placeholder="Reason..." 
                        value={updateAssessmentClosedAtData.reason} 
                        onChange={(e) => setUpdateAssessmentClosedAtData({...updateAssessmentClosedAtData, reason: e.target.value})} 
                      />
                    </div>
                  </div>
                  <div className="card-content text-end">
                    <Button onClick={updateAssessmentClosedAt} color="blue" text="Update" />
                  </div>  
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Status Change Modal */}
      {changeAssessmentStatusModalVisible && changeAssessmentStatusModalData && (
        <div className="fixed-bottom modal-bg" onClick={(e) => setChangeAssessmentStatusModalVisible(false)}>
          <div className="container-fluid" style={{paddingTop: '5%'}}>
            <div className="row">
              <div className="col-xs-10 col-xs-offset-1 col-lg-4 col-lg-offset-4">
                <div className="card shadowmore" onClick={(e) => {e.stopPropagation()}}>
                  <div className="card-header">
                    <span className="typo-header-3xLarge">Change Assessment Status</span><br/>
                    <span className="typo-normal-175">Select reason and click confirm button</span>
                  </div>
                  <div className="card-content">
                    {changeAssessmentStatusModalData.status.reasons.map((reason, ri) => {
                      return (
                        <div 
                          key={"reason_" + ri} 
                          className="side-item small typo-medium-150" 
                          onClick={() => changeAssessmentStatusConfirm(changeAssessmentStatusModalData.assessmentID, changeAssessmentStatusModalData.status.id, reason.id)}
                        >
                          {reason.title}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Assessment;
